/**
 * ReseedEvent types and API instance
 *
 * For data fetching and mutations, see: src/model/useReseedEventQueries.ts
 */

import { API } from "common/utils";
import { AMOUNT_INPUT_MODE, MACHINERY } from "./commonTypes";
import { GRASS_TYPE } from "./fieldTypes";

export const FIELD_RESEEDED_EVENT_REJECTION_PERIOD_DAYS = 30;

export enum SEEDING_METHOD {
  // Legacy values (kept for backwards compatibility)
  PLOUGH_AND_POWER_HARROW = "PLOUGH_AND_POWER_HARROW",
  DIRECT_DRILL = "DIRECT_DRILL",
  SHALLOW_CULTIVATION = "SHALLOW_CULTIVATION",
  OVERSEED = "OVERSEED",
  // Reseed methods
  PLOUGH_POWER_HARROW_DRILL = "PLOUGH_POWER_HARROW_DRILL",
  PLOUGH_SHALLOW_CULTIVATE_DRILL = "PLOUGH_SHALLOW_CULTIVATE_DRILL",
  PLOUGH_CULTIPACK_SEED = "PLOUGH_CULTIPACK_SEED",
  PLOUGH_DIRECT_DRILL = "PLOUGH_DIRECT_DRILL",
  SHALLOW_CULTIVATE_DRILL = "SHALLOW_CULTIVATE_DRILL",
  DIRECT_DRILL_RESEED = "DIRECT_DRILL_RESEED",
  // Overseed methods
  DIRECT_DRILL_OVERSEED = "DIRECT_DRILL_OVERSEED",
  TINE_DRILL_OVERSEED = "TINE_DRILL_OVERSEED",
  BROADCAST_OVERSEED = "BROADCAST_OVERSEED",
  FROST_SEEDING = "FROST_SEEDING",
}

/** New seeding methods to show in the UI dropdown */
export const SEEDING_METHOD_OPTIONS: SEEDING_METHOD[] = [
  SEEDING_METHOD.PLOUGH_POWER_HARROW_DRILL,
  SEEDING_METHOD.PLOUGH_SHALLOW_CULTIVATE_DRILL,
  SEEDING_METHOD.PLOUGH_CULTIPACK_SEED,
  SEEDING_METHOD.PLOUGH_DIRECT_DRILL,
  SEEDING_METHOD.SHALLOW_CULTIVATE_DRILL,
  SEEDING_METHOD.DIRECT_DRILL_RESEED,
  SEEDING_METHOD.DIRECT_DRILL_OVERSEED,
  SEEDING_METHOD.TINE_DRILL_OVERSEED,
  SEEDING_METHOD.BROADCAST_OVERSEED,
  SEEDING_METHOD.FROST_SEEDING,
];

/** Derive reseed type from seeding method */
export function getReseedTypeFromMethod(method: SEEDING_METHOD): RESEED_TYPE {
  switch (method) {
    case SEEDING_METHOD.DIRECT_DRILL_OVERSEED:
    case SEEDING_METHOD.TINE_DRILL_OVERSEED:
    case SEEDING_METHOD.BROADCAST_OVERSEED:
    case SEEDING_METHOD.FROST_SEEDING:
    case SEEDING_METHOD.OVERSEED:
      return RESEED_TYPE.OVERSEED;
    default:
      return RESEED_TYPE.RESEED;
  }
}

export enum RESEED_TYPE {
  RESEED = "RESEED",
  OVERSEED = "OVERSEED",
}

export enum RESEED_TOTAL_AMOUNT_UNIT {
  G = "G",
  KG = "KG",
  TONNE = "TONNE",
}

export type ReseedEventBase = {
  note: string;
  reseedDate: string;
  newGrassType: GRASS_TYPE;
  seedingMethod: SEEDING_METHOD;
  machinery?: MACHINERY[];
  fuel?: number;
  operationalCost?: number;
  operationalCostUnit?: string;
  productCost?: number;
  productCostUnit?: string;
  reseedType: RESEED_TYPE;
  seedingRate?: number;
  productName?: string;
  totalAmount?: number;
  totalAmountUnit?: RESEED_TOTAL_AMOUNT_UNIT;
  // Whether the user entered a seeding rate (kg/ha) or an absolute total.
  // Both seedingRate and totalAmount are persisted; this records intent so the
  // edit form reopens in the same mode. Defaults to RATE for legacy events.
  amountInputMode?: AMOUNT_INPUT_MODE;
  fieldGuids: string[];
};

export type ReseedEvent = ReseedEventBase & {
  guid: string;
  creationDate?: string;
};

class ReseedAPI extends API<ReseedEvent> {
  async getAllReseedEventsForFarm(
    guid: string,
    reseedTimeAfter?: string
  ): Promise<ReseedEvent[]> {
    const reseedTimeAfterParam = reseedTimeAfter
      ? `&reseedTimeAfter=${reseedTimeAfter}`
      : "";
    const res = await fetch(
      `${this.baseUrl}reseeds?farmGuid=${guid}${reseedTimeAfterParam}`,
      {
        method: "GET",
        headers: {
          Authorization: `Bearer ${window.localStorage.getItem("jwt") || ""}`,
          "Content-Type": "application/json",
        },
      }
    );
    return (await res.json()) as ReseedEvent[];
  }

  async createReseedsEventForFarm(
    reseedsEvent: ReseedEventBase
  ): Promise<{ guid: string }> {
    const res = await fetch(`${this.baseUrl}reseeds`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${window.localStorage.getItem("jwt") || ""}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(reseedsEvent),
    });
    return (await res.json()) as { guid: string };
  }
}

export const reseedApi = new ReseedAPI("reseeds", "v2");

export const createReseedsEventForFarm = async (
  reseedsEvent: ReseedEventBase
) => {
  await reseedApi.createReseedsEventForFarm(reseedsEvent);
};
